import { Loading } from '@/components/shared';
import { useDirectScheduleAdjustmentTimeframeQuery } from '@/hooks/apis';
import { UploadedFile } from '@/components/shared/file-upload/types';
import { Order, ScheduleAdjustmentTimeframe, ScheduleAdjustmentTimeframeFrame } from '@/types';
import { bem } from '@/utilities';
import dayjs from 'dayjs';
import ja from 'dayjs/locale/ja';
import React, { useState } from 'react';
import { OrderRequestModal } from './orderRequestModal';

dayjs.locale(ja);

const TitleHeader: React.FC = () => {
  return (
    <>
      <h1 className={bem('schedule-selection', 'timeframe-title')}>2 ご希望の日程と時間帯の選択</h1>
      <div className={bem('schedule-selection', 'timeframe-descriptions')}>
        <p className={bem('schedule-selection', 'timeframe-description')}>
          予約可能の候補日程です。ご都合の良い時間帯を選択してください。時間は作業完了までの時間を含めます。
        </p>
        <p className={bem('schedule-selection', 'timeframe-description')}>
          開始時間は時間帯選択後に表示される画面で確定後に表示されます。
        </p>
        <p className={bem('schedule-selection', 'timeframe-description', { gray: true })}>
          ※選択ボタンが押せない・日程が表示されない場合は既に他の予定が入っています。
        </p>
        <p className={bem('schedule-selection', 'timeframe-description', { gray: true })}>
          ※ご希望の日程時間が出ない場合は、「右下のお問合せ」からご連絡ください。
        </p>
      </div>
      <p className={bem('schedule-selection', 'timeframe-available')}>
        <span>○：予約可能</span>
        <span>×：予約不可</span>
      </p>
    </>
  );
};

type TimeframeHeaderProps = {
  startMonth: string;
  endMonth: string;
  selectedMonth: string;
  setSelectedMonth: (month: string) => void;
};
const TimeframeHeader: React.FC<TimeframeHeaderProps> = (props) => {
  const { startMonth, endMonth, selectedMonth, setSelectedMonth } = props;
  const monthCount = Math.max(
    0,
    dayjs(endMonth).diff(dayjs(startMonth), "month") + 1
  );

  return (
    <div className={bem('schedule-selection', 'timeframe-header')}>
      {[...Array(monthCount)]
        .map((_, i) => dayjs(startMonth).add(i, 'M'))
        .map((month) => {
          const formattedMonth = month.format('YYYY/MM');
          const displayMonth = month.format('M');
          return (
            <p
              key={formattedMonth}
              className={bem('schedule-selection', 'timeframe-header__month', {
                active: formattedMonth == selectedMonth
              })}
              onClick={() => setSelectedMonth(formattedMonth)}
            >
              {displayMonth}月
            </p>
          );
        })}
    </div>
  );
};

type TimeframeBodyProps = {
  scheduleAdjustmentTimeframe: ScheduleAdjustmentTimeframe;
  selectedMonth: string;
  onClickAvailableTimeframe: (availableTimeframeStart: string, availableTimeframeEnd: string) => void;
};
const TimeframeBody: React.FC<TimeframeBodyProps> = (props) => {
  const { scheduleAdjustmentTimeframe, selectedMonth, onClickAvailableTimeframe } = props;

  if (!scheduleAdjustmentTimeframe) {
    return (
      <div className={bem('loading', 'content')}>
        {[...Array(8)].map((_, i) => (
          <div className={bem('loading', 'content-spinner')} key={i}></div>
        ))}
      </div>
    );
  }

  if (scheduleAdjustmentTimeframe.dates.length <= 0) {
    const monthLabel = dayjs(selectedMonth).format('M');
    return (
      <p style={{ textAlign: 'center' }}>
        {monthLabel}月の整備士の空き枠が大変少なくなっております。他の月をご確認ください。
        <br />
        他の月もない場合は右下のお問合せよりご連絡をお願いいたします。
      </p>
    );
  }

  return (
    <div className={bem('schedule-selection', 'timeframe-body')}>
      {scheduleAdjustmentTimeframe?.dates
        ?.sort((a, b) => (dayjs(a.date).isAfter(b.date) ? 1 : -1))
        ?.map((timeframe) => {
          const allDayFrame = timeframe.timeframes.find((frame) => frame.isAllDay);
          const sortedFrames = timeframe.timeframes
            .filter((frame) => !frame.isAllDay)
            .sort((a, b) => a.startMinutes - b.startMinutes);

          return (
            <TimeframeItem
              key={timeframe.date}
              date={timeframe.date}
              allDayFrame={allDayFrame}
              frames={sortedFrames}
              onClickAvailableTimeframe={(availableTimeframeStart, availableTimeframeEnd) =>
                onClickAvailableTimeframe(availableTimeframeStart, availableTimeframeEnd)
              }
            />
          );
        })}
    </div>
  );
};

type TimeframeItemProps = {
  date: string;
  allDayFrame: ScheduleAdjustmentTimeframeFrame;
  frames: ScheduleAdjustmentTimeframeFrame[];
  onClickAvailableTimeframe: (availableTimeframeStart: string, availableTimeframeEnd: string) => void;
};
const TimeframeItem: React.FC<TimeframeItemProps> = (props) => {
  const { date, allDayFrame, frames, onClickAvailableTimeframe } = props;
  const [openFrames, setOpenFrames] = useState<boolean>(false);

  const changeDisplayMinutes = (minutes: number) => {
    return minutes / 60;
  };

  return (
    <div className={bem('schedule-selection', 'timeframe-body__item')}>
      <div
        className={bem('schedule-selection', 'timeframe-body__date', {
          blue: dayjs(date).day() == 6,
          red: dayjs(date).day() == 0
        })}
      >
        <p>{dayjs(date).format('MM/DD')}</p>
        <p>{dayjs(date).format('（dd）')}</p>
      </div>
      <div className={bem('schedule-selection', 'timeframe-body__buttons')}>
        <div className={bem('schedule-selection', 'timeframe-body__all')}>
          {allDayFrame && (
            <>
              <p
                className={bem('schedule-selection', 'timeframe-body__button', {
                  allday: true,
                  active: allDayFrame.available,
                  disabled: !allDayFrame.available
                })}
                onClick={() => {
                  if (!allDayFrame.available) return;
                  onClickAvailableTimeframe(
                    dayjs(date).add(allDayFrame.startMinutes, 'm').format('YYYY-MM-DDTHH:mm'),
                    dayjs(date).add(allDayFrame.endMinutes, 'm').format('YYYY-MM-DDTHH:mm')
                  );
                }}
              >
                ○ 終日 ({changeDisplayMinutes(allDayFrame.startMinutes)}時～
                {changeDisplayMinutes(allDayFrame.endMinutes)}時)
              </p>
              <div className={bem('schedule-selection', 'timeframe-body__line')}></div>
            </>
          )}
          <p
            className={bem('schedule-selection', 'timeframe-body__toggle', { open: !openFrames, close: openFrames })}
            onClick={() => setOpenFrames(!openFrames)}
          >
            時間を指定
          </p>
        </div>
        <div className={bem('schedule-selection', 'timeframe-body__frames', { open: openFrames })}>
          {frames.map((frame, index) => (
            <div
              className={bem('schedule-selection', 'timeframe-body__button', {
                frame: true,
                active: frame.available,
                disabled: !frame.available
              })}
              key={index}
              onClick={() => {
                if (!frame.available) return;
                onClickAvailableTimeframe(
                  dayjs(date).add(frame.startMinutes, 'm').format('YYYY-MM-DDTHH:mm'),
                  dayjs(date).add(frame.endMinutes, 'm').format('YYYY-MM-DDTHH:mm')
                );
              }}
            >
              <p>{frame.available ? '○' : '×'}</p>
              <p>
                {changeDisplayMinutes(frame.startMinutes)}時～{changeDisplayMinutes(frame.endMinutes)}時
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

type Props = {
  token: string;
  orderId?: number;
  openOrderRequestModal: boolean;
  orders: Order[];
  setOpenOrderRequestModal: (openOrderRequestModal: boolean) => void;
  onClickSubmitButton: (
    contactName: string,
    contactPhoneNumber: string,
    parkingType: string,
    comment: string,
    selectedAvailableTimeframeStart: string,
    selectedAvailableTimeframeEnd: string,
    uploadedFiles: UploadedFile[]
  ) => void;
};

export const ScheduleTimeframe: React.FC<Props> = (props) => {
  const {
    token,
    orderId,
    openOrderRequestModal,
    orders,
    setOpenOrderRequestModal,
    onClickSubmitButton
  } = props;
  const selectedOrder = orders.find(order => order.id === orderId);
  const startMonth = dayjs(selectedOrder?.assignableStartDate).isBefore(dayjs())
    ? dayjs().format('YYYY/MM')
    : dayjs(selectedOrder?.assignableStartDate).format('YYYY/MM');
  const endMonth = dayjs(selectedOrder?.assignableEndDate).format('YYYY/MM');
  const [selectedMonth, setSelectedMonth] = useState<string>(startMonth);
  const [selectedAvailableTimeframeStart, setSelectedAvailableTimeframeStart] = useState<string>();
  const [selectedAvailableTimeframeEnd, setSelectedAvailableTimeframeEnd] = useState<string>();
  const timeframeQuery = useDirectScheduleAdjustmentTimeframeQuery({ orderId, token, month: selectedMonth });
  const scheduleAdjustmentTimeframe = timeframeQuery.data;

  return (
    <div className={bem('schedule-selection', 'timeframe')}>
      <TitleHeader />
      {timeframeQuery.isFetching ? (
        <Loading />
      ) : (
        <div className={bem('schedule-selection', 'timeframe-table')}>
          <TimeframeHeader
            startMonth={startMonth}
            endMonth={endMonth}
            selectedMonth={selectedMonth}
            setSelectedMonth={(month) => setSelectedMonth(month)}
          />
          <TimeframeBody
            scheduleAdjustmentTimeframe={scheduleAdjustmentTimeframe}
            selectedMonth={selectedMonth}
            onClickAvailableTimeframe={(availableTimeframeStart, availableTimeframeEnd) => {
              setSelectedAvailableTimeframeStart(availableTimeframeStart);
              setSelectedAvailableTimeframeEnd(availableTimeframeEnd);
              setOpenOrderRequestModal(true);
            }}
          />
        </div>
      )}
      <OrderRequestModal
        openModal={openOrderRequestModal}
        onClickCloseModal={() => setOpenOrderRequestModal(false)}
        order={orders.find((order) => order.id === orderId)}
        selectedAvailableTimeframeStart={selectedAvailableTimeframeStart}
        selectedAvailableTimeframeEnd={selectedAvailableTimeframeEnd}
        onClickSubmitButton={(contactName: string, contactPhoneNumber: string, parkingType: string, comment: string, uploadedFiles: UploadedFile[]) =>
          onClickSubmitButton(
            contactName,
            contactPhoneNumber,
            parkingType,
            comment,
            selectedAvailableTimeframeStart,
            selectedAvailableTimeframeEnd,
            uploadedFiles
          )
        }
      />
    </div>
  );
};
